import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { UserProfileComponent } from '../userprofile/userprofile';
import { PortfolioSelectorComponent } from '../shared/portfolio-selector/portfolio-selector';
import { PortfolioStateService } from '../shared/portfolio-state.service';
import type { CommonPortfolioSearchResult } from '../shared/commonportfolio.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CurrentUserService } from '../shared/current-user.service';

@Component({
  selector: 'app-navbar',
  imports: [RouterLink, RouterLinkActive, UserProfileComponent, PortfolioSelectorComponent],
  templateUrl: './navbar.html',
  styleUrl: './navbar.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavbarComponent {
  // Feature switches, read straight from the environment so the template can gate on them.
  // The matching routes are guarded too, so hiding a control also closes its URL.
  protected readonly features = environment.features;
  // 369526: Super-Admin-only entries hide for everyone else. No route guard on purpose — the
  // signal resolves async, so a guard would bounce real super admins on cold-load deep links;
  // typed URLs are already handled by the server's 403 → access-denied panel.
  protected readonly isSuperAdmin = inject(CurrentUserService).isSuperAdmin;
  private readonly router = inject(Router);
  private readonly portfolioState = inject(PortfolioStateService);

  /** Drives the selector's display, so it survives navigation and full page reloads. */
  protected readonly currentPortfolio = this.portfolioState.portfolio;
  protected readonly selectorClearTrigger = signal(0);

  protected readonly expanded = signal(false);
  protected readonly dropdownOpen = signal(false);
  protected readonly reportsDropdownOpen = signal(false);
  protected readonly buildNumber = signal<string>('');

  protected readonly isLoginPage = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(e => (e as NavigationEnd).urlAfterRedirects.startsWith('/login')),
      startWith(this.router.url.startsWith('/login'))
    ),
    { initialValue: this.router.url.startsWith('/login') }
  );

  constructor() {
    // Extract build number from HTML comment
    const htmlComment = document.documentElement.outerHTML;
    const match = htmlComment.match(/Build Number: ([\d.]+)/);
    if (match && match[1]) {
      this.buildNumber.set(match[1]);
    } else {
      this.buildNumber.set('Unknown');
    }
  }

  toggle() {
    this.expanded.update(v => !v);
  }

  collapse() {
    this.expanded.set(false);
    this.dropdownOpen.set(false);
    this.reportsDropdownOpen.set(false);
  }

  toggleDropdown(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.dropdownOpen.update(open => !open);
    // Close other dropdowns
    this.reportsDropdownOpen.set(false);
  }

  toggleReportsDropdown(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.reportsDropdownOpen.update(open => !open);
    // Close other dropdowns
    this.dropdownOpen.set(false);
  }

  closeDropdown() {
    this.dropdownOpen.set(false);
    this.reportsDropdownOpen.set(false);
  }

  /**
   * Selecting a group from the banner routes to it, exactly as the dashboard header used to.
   * State is set before navigating so portfolioGroupName from the search result is not lost.
   */
  protected onPortfolioSelected(port: CommonPortfolioSearchResult): void {
    this.portfolioState.select(port);
    this.router.navigate(['/dashboard', port.portfolioGroupId]);
  }

  protected onPortfolioCleared(): void {
    try { sessionStorage.removeItem('dashboard.lastPortfolioGroupId'); } catch { /* ignore */ }
    this.portfolioState.clear();
    this.selectorClearTrigger.update(v => v + 1);
    this.router.navigate(['/dashboard']);
  }
}
